import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { clearTokens } from "../utils/token";

let isRefreshing = false;
let refreshSubscribers: Array<() => void> = [];

function onTokenRefreshed() {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: () => void) {
  refreshSubscribers.push(cb);
}

function generateRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * 后端 HTTP 客户端。
 * 所有 API 请求使用绝对路径（如 /api/v1/chain/wallets/list），
 * 由 Vite proxy (dev) 或 Nginx (production) 原样转发到后端。
 */
class BackendClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      withCredentials: true,
      timeout: 60000,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        config.headers['X-Request-Id'] = generateRequestId();
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const requestUrl = String(originalRequest?.url || '');

        if (error.response?.status === 401
          && originalRequest
          && !originalRequest._retry
          && !requestUrl.includes('/auth/api/v1/auth/refresh')
          && !requestUrl.includes('/api/v1/auth/refresh')
          && !requestUrl.includes('/auth/api/v1/auth/check')
          && !requestUrl.includes('/api/v1/auth/check')) {

          if (isRefreshing) {
            return new Promise((resolve) => {
              addRefreshSubscriber(() => {
                resolve(this.client(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            await axios.post('/auth/api/v1/auth/refresh', {}, { withCredentials: true });
            isRefreshing = false;
            onTokenRefreshed();
            return this.client(originalRequest);
          } catch (refreshError) {
            isRefreshing = false;
            clearTokens();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  getClient(): AxiosInstance {
    return this.client;
  }
}

export const backendClient = new BackendClient();
export default backendClient;
